import { useClipboard } from '@chakra-ui/react';
import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import {
  CopyIcon,
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownDivider,
  DropdownItem,
  EnvelopeIcon,
  IconButton,
  MoreIcon,
  Paragraph,
  PlusIcon,
  TrashIcon,
  useSnackbar,
} from 'ui-components';
import { SavedList } from '~/clientApi/savedLists.model';
import { FEATURE_FLAGS } from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import { useFetchCreator } from '~/hooks/api/useFetchCreator';
import { useCreatorCopyEmbed } from '~/hooks/useCreatorCopyEmbed';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { useTalentManagerAccess } from '~/hooks/useTalentManagerAccess';
import { useWebLinksStore } from '~/screens/ManageWebLinks/useWebLinksStore';
import { useEditCreatorStore } from '~/screens/MyAgency/store';
import { ICreator, ManagerShortType } from '~/types';
import { useCurrentUserAccess } from '~/userManagementApi/userAccess';
import { MeResponse } from '~/userManagementApi/userAccess.types';

import { useCreatorActionModal } from '../CreatorActionModal/CreatorActionModal.context';

interface TalentDropdownMenuV2Props {
  creator: ICreator;
  list?: SavedList;
  onEditNote?: (creator: ICreator) => void;
  onOpenChange?: (isOpen: boolean) => void;
  onRemoveFromList?: (creator: ICreator) => void;
}

export function TalentDropdownMenuV2({
  creator,
  list,
  onEditNote,
  onOpenChange,
  onRemoveFromList,
}: TalentDropdownMenuV2Props) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { data: { manager, ytChannels } = {} } = useFetchCreator(
    isDropdownOpen ? creator?.id : undefined, // Do not fetch if the dropdown is closed
  );

  const userAccessData = useCurrentUserAccess({
    entityId: manager?.agency?.id,
  });

  const { shouldHaveAccessAsManagerComanagerAdmin } = useTalentManagerAccess(
    creator,
    userAccessData.data as MeResponse,
  );

  const { onBasicClick, onDetailClick } = useCreatorCopyEmbed({
    ...creator,
    ytChannels,
  });

  const creatorManager = manager || creator?.manager;

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isDropdownOpen);
    }
  }, [isDropdownOpen, onOpenChange]);

  return (
    <Dropdown
      onDropdownOpen={() => setIsDropdownOpen(true)}
      onDropdownClose={() => setIsDropdownOpen(false)}
    >
      {({ isOpen, buttonRef, getReferenceProps }) => (
        <>
          <IconButton
            aria-label="Open creator invitation actions"
            icon={<MoreIcon boxSize={4} />}
            isActive={isOpen}
            ref={buttonRef}
            size="md"
            variant="transparent"
            {...getReferenceProps()}
            onClick={(e) => {
              e.stopPropagation();
              setIsDropdownOpen(isOpen ?? false);
            }}
          />
          <DropdownContent
            __css={{
              zIndex: 1099, // Required to fix my agency header z-index issue
            }}
          >
            <CopyEmbedDropdownItem
              creator={creator}
              onBasicClick={onBasicClick}
              onDetailClick={onDetailClick}
            />
            <DropdownDivider />
            <ViewProfileDropdownItem creator={creator} isShared={false} />
            <ShareProfileDropdownItem slug={creator.slug} />
            <AddToListDropdownItem creator={creator} />
            <ManagerContactDropdownItem
              manager={creatorManager}
              title="Talent Manager"
              isShared={false}
            />

            {shouldHaveAccessAsManagerComanagerAdmin && (
              <>
                <DropdownDivider />
                <EditInfoDropdownItem creator={creator} />
                <WebLinksDropdownItem creator={creator} />
                <EditPlatformsDropdownItem creator={creator} />
              </>
            )}

            <ListDropdownMenu
              creator={creator}
              list={list}
              onEditNote={onEditNote}
              onRemoveFromList={onRemoveFromList}
            />
          </DropdownContent>
        </>
      )}
    </Dropdown>
  );
}

function CopyEmbedDropdownItem({
  creator,
  onBasicClick,
  onDetailClick,
}: {
  creator: ICreator;
  onBasicClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onDetailClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <>
      <DropdownItem
        onClick={onBasicClick}
        trackId="wm-creator-copy-basic-embed"
      >
        <CopyIcon boxSize={4} />
        <Paragraph size="md">Copy Basic Embed</Paragraph>
      </DropdownItem>

      {!creator?.isOffPlatform && creator?.hasConnectedPlatform && (
        <DropdownItem
          track-id="wm-creator-copy-detail-embed"
          onClick={onDetailClick}
        >
          <CopyIcon boxSize={4} />
          <Paragraph size="md">Copy Detail Embed</Paragraph>
        </DropdownItem>
      )}
    </>
  );
}

function AddToListDropdownItem({ creator }: { creator: ICreator }) {
  const { setCreators, setIsAddToListModalOpen } = useCreatorActionModal();

  const handleAddToList = () => {
    setCreators([creator]);
    setIsAddToListModalOpen(true);
  };

  return (
    <DropdownItem
      onClick={handleAddToList}
      trackId="wm-add-single-talent-to-list"
    >
      <PlusIcon boxSize={4} />
      Add to a List
    </DropdownItem>
  );
}

function ListDropdownMenu({
  creator,
  list,
  onEditNote,
  onRemoveFromList,
}: {
  creator: ICreator;
  list?: SavedList;
  onEditNote?: (creator: ICreator) => void;
  onRemoveFromList?: (creator: ICreator) => void;
}) {
  if (list && onRemoveFromList) {
    return (
      <>
        <DropdownDivider />

        <EditNoteDropdownItem creator={creator} onEditNote={onEditNote} />

        {!list.name.match(/Roster$/) && (
          <DropdownItem
            onClick={() => onRemoveFromList(creator)}
            trackId="wm-remove-single-talent-from-list"
          >
            <TrashIcon boxSize={4} />
            Remove from list
          </DropdownItem>
        )}
      </>
    );
  }

  return null;
}

function ManagerContactDropdownItem({
  manager,
  title,
  isShared,
}: {
  manager: ManagerShortType;
  title?: string;
  isShared: boolean;
}) {
  const snackbar = useSnackbar();
  const { onCopy } = useClipboard(manager?.email);

  const handleManagerClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    onCopy();

    snackbar.success({
      description: 'Email Copied!',
      duration: 2000,
      isCloseable: true,
    });
  };

  if (!manager?.id) return null;

  return (
    <Dropdown placement="right">
      {({ buttonRef, getReferenceProps }) => (
        <>
          {!isShared ? (
            <DropdownButton>
              <EnvelopeIcon boxSize={4} />
              {title}
            </DropdownButton>
          ) : (
            <IconButton
              aria-label="Open menu"
              icon={<EnvelopeIcon boxSize={4} />}
              ref={buttonRef}
              size="md"
              variant="transparent"
              {...getReferenceProps()}
            />
          )}
          <DropdownContent>
            {manager ? (
              <DropdownItem
                onClick={(e) => handleManagerClick(e)}
                trackId="wm-talent-copy-manager-email"
              >
                {/* Default to email for managers that haven't registered yet */}
                <EnvelopeIcon boxSize={4} />
                {manager.name || manager.email}
              </DropdownItem>
            ) : null}
          </DropdownContent>
        </>
      )}
    </Dropdown>
  );
}

function ShareProfileDropdownItem({ slug }: { slug: string }) {
  const snackbar = useSnackbar();

  const { onCopy } = useClipboard(`${slug}.foam.io`);
  const handleClickShareProfile = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onCopy();
      snackbar.success({ description: 'Creator link copied to clipboard' });
    },
    [onCopy, snackbar],
  );
  return (
    <DropdownItem aria-label="Share Creator" onClick={handleClickShareProfile}>
      <Icon name="Share" />
      Share Profile
    </DropdownItem>
  );
}

function ViewProfileDropdownItem({
  creator,
  isShared,
}: {
  creator: ICreator;
  isShared: boolean;
}) {
  const router = useRouter();

  const handleClick = useCallback(async () => {
    if (isShared) {
      trackEvent('foam-view-creator-from-talent-stacked-view');
      router.push(`/shared/talent/${creator.slug}`);
      return;
    }

    trackEvent('foam-view-creator-from-talent-stacked-view');
    router.push(`/t/${creator.slug}`);
  }, [creator, isShared, router]);

  return (
    <DropdownItem
      track-id="foam-view-creator-from-talent-page"
      onClick={handleClick}
    >
      <Icon name="Person" />
      View Profile
    </DropdownItem>
  );
}

function WebLinksDropdownItem({ creator }: { creator: ICreator }) {
  const { isEnabled } = useFeature(FEATURE_FLAGS.foamWebLinks);

  const { showWebLinks } = useWebLinksStore();

  const onCreatorWebLinksClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      showWebLinks(creator);
    },
    [creator, showWebLinks],
  );

  if (!isEnabled) return null;

  return (
    <DropdownItem
      onClick={onCreatorWebLinksClick}
      trackId="foam-talent-menu-edit-weblinks"
    >
      <Icon name="Link" />
      Links
    </DropdownItem>
  );
}

function EditInfoDropdownItem({ creator }: { creator: ICreator }) {
  const { openEditModal, openEditDrawer } = useEditCreatorStore();

  const isFoamUnverifiedToVerified =
    useFeatureFlag(FEATURE_FLAGS.foamUnverifiedToVerified) === 'enabled';

  const onEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (isFoamUnverifiedToVerified) {
        openEditModal(creator);
        return;
      }

      openEditDrawer(creator);
    },
    [creator, isFoamUnverifiedToVerified, openEditModal, openEditDrawer],
  );

  return (
    <DropdownItem onClick={onEditClick} trackId="foam-talent-menu-edit-info">
      <Icon name="Edit" />
      Edit Info
    </DropdownItem>
  );
}

function EditPlatformsDropdownItem({ creator }: { creator: ICreator }) {
  const router = useRouter();

  const { isEnabled } = useFeature(FEATURE_FLAGS.foamOffPlatformAddSocials);

  const handleClick = () => {
    router.push(`/t/${creator?.slug}/platform-connections`);
  };

  if (!isEnabled) return null;

  return (
    <DropdownItem
      onClick={handleClick}
      trackId="foam-talent-menu-edit-platforms"
    >
      <Icon name="Edit" />
      Edit Platforms
    </DropdownItem>
  );
}

function EditNoteDropdownItem({
  creator,
  onEditNote,
}: {
  creator: ICreator;
  onEditNote?: (creator: ICreator) => void;
}) {
  const handleEditNote = useCallback(() => {
    onEditNote?.(creator);
  }, [creator, onEditNote]);

  if (!onEditNote) return null;

  return (
    <DropdownItem onClick={handleEditNote} trackId="foam-talent-menu-edit-note">
      <Icon name="Edit" />
      {creator.notes ? 'Edit Note' : 'Add Note'}
    </DropdownItem>
  );
}
